/**
 * Mirrors public.roles / public.permissions (supabase/migrations/…_identity_access.sql).
 * The database is the authority — these types only give the UI compile-time safety.
 */
export const ROLE_CODES = [
  'owner',
  'system_admin',
  'finance',
  'hr',
  'marketing',
  'workshop',
  'inventory',
  'logistics',
  'pos',
  'qa',
  'auditor',
  'employee',
  'franchise',
  'vendor',
  'customer',
  'shipment_customer',
] as const;

export type RoleCode = (typeof ROLE_CODES)[number];

export type PermissionCode =
  | 'customers.read_all'
  | 'customers.manage'
  | 'vehicles.read_all'
  | 'vehicles.manage'
  | 'vehicle_master.manage'
  | 'catalog.read_internal'
  | 'catalog.manage'
  | 'catalog.approve_specs'
  | 'warranties.read_all'
  | 'warranties.manage'
  | 'services.manage'
  | 'bookings.read_all'
  | 'bookings.manage'
  | 'sales.read_all'
  | 'sales.manage'
  | 'pos.sell'
  | 'pos.read'
  | 'pos.price_override'
  | 'pos.void'
  | 'payments.read_all'
  | 'payments.manage'
  | 'promotions.manage'
  | 'collections.read_all'
  | 'collections.manage'
  | 'inventory.read'
  | 'inventory.manage'
  | 'conversion.manage'
  | 'workshop.read'
  | 'workshop.manage'
  | 'procurement.manage'
  | 'logistics.read'
  | 'logistics.manage'
  | 'franchise.read_all'
  | 'franchise.manage'
  | 'hr.read_all'
  | 'hr.manage'
  | 'hr.leave_approve'
  | 'marketing.campaigns_manage'
  | 'marketing.analytics_read'
  | 'executive.analytics_read'
  | 'reports.read'
  | 'audit.read'
  | 'files.manage'
  | 'users.read'
  | 'users.manage'
  | 'roles.manage'
  | 'odoo.read'
  | 'odoo.manage'
  | 'branches.manage'
  | 'settings.manage'
  | 'integrations.manage'
  | 'notifications.send'
  // Final portals pass (…_access_model.sql)
  | 'portal.executive'
  | 'portal.admin'
  | 'portal.finance'
  | 'portal.hr'
  | 'portal.marketing'
  | 'portal.workshop'
  | 'portal.logistics'
  | 'portal.inventory'
  | 'portal.pos'
  | 'portal.qa'
  | 'portal.audit'
  | 'portal.employee'
  | 'portal.preview'
  | 'users.invite'
  | 'users.security'
  | 'users.export'
  | 'access.bundles_manage'
  | 'access.overrides_manage'
  | 'customers.export'
  | 'bookings.assign'
  | 'sales.export'
  | 'payments.refund_approve'
  | 'collections.assign'
  | 'collections.export'
  | 'inventory.export'
  | 'workshop.assign'
  | 'suppliers.read'
  | 'suppliers.manage'
  | 'logistics.assign'
  | 'hr.export'
  | 'marketing.campaigns_approve'
  | 'marketing.export'
  | 'marketing.leads_read'
  | 'marketing.leads_manage'
  | 'marketing.leads_assign'
  | 'reports.export'
  | 'qa.read'
  | 'qa.manage'
  | 'audit.findings_manage'
  | 'audit.export'
  | 'support.read'
  | 'support.manage'
  | 'support.assign'
  | 'assistant.knowledge_manage'
  // Company identities (…_hr_identity_lifecycle.sql)
  | 'identity.read'
  | 'identity.manage'
  // Communication Center / Teams (…_communication_center.sql)
  | 'communications.read_all';

export type AccountType = 'customer' | 'staff' | 'business_client' | 'shipment_customer' | 'supplier';

/** A permission held for one branch or center only (RLS narrows the rows). */
export interface ScopedGrant {
  permission: PermissionCode;
  scope: 'branch' | 'center';
  branchId: string | null;
  centerId: string | null;
}

/** Result of the get_my_access() RPC. */
export interface AccessProfile {
  userId: string;
  fullName: string;
  email: string | null;
  preferredLanguage: 'ar' | 'en';
  accountType: AccountType;
  status: 'active' | 'suspended' | 'disabled';
  roles: RoleCode[];
  /** Held at any scope — drives navigation. The database narrows scoped rows. */
  permissions: PermissionCode[];
  primaryPortal: string | null;
  /** Held for the whole business. */
  globalPermissions?: PermissionCode[];
  scopes?: ScopedGrant[];
  bundles?: string[];
  inheritedRoles?: RoleCode[];
  /** An administrator requires a new password before anything else. */
  passwordResetRequired?: boolean;
  /** Set while an administrator previews a portal as this role (read-only). */
  preview?: RoleCode | null;
}

function isRole(value: unknown): value is RoleCode {
  return typeof value === 'string' && (ROLE_CODES as readonly string[]).includes(value);
}

/** Parses the RPC payload defensively (unknown roles are dropped, never trusted). */
export function parseAccessProfile(raw: unknown): AccessProfile | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.user_id !== 'string') return null;
  const roles = Array.isArray(r.roles) ? r.roles.filter(isRole) : [];
  const permissions = Array.isArray(r.permissions)
    ? (r.permissions.filter((p) => typeof p === 'string') as PermissionCode[])
    : [];
  const status = r.status === 'suspended' || r.status === 'disabled' ? r.status : 'active';
  const accountType: AccountType =
    r.account_type === 'staff' ||
    r.account_type === 'business_client' ||
    r.account_type === 'shipment_customer' ||
    r.account_type === 'supplier'
      ? r.account_type
      : 'customer';
  const strings = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
  const scopes: ScopedGrant[] = Array.isArray(r.scopes)
    ? r.scopes.flatMap((raw): ScopedGrant[] => {
        const g = raw as Record<string, unknown>;
        if (typeof g.permission !== 'string' || (g.scope !== 'branch' && g.scope !== 'center')) return [];
        return [
          {
            permission: g.permission as PermissionCode,
            scope: g.scope,
            branchId: typeof g.branch_id === 'string' ? g.branch_id : null,
            centerId: typeof g.center_id === 'string' ? g.center_id : null,
          },
        ];
      })
    : [];
  return {
    userId: r.user_id,
    fullName: typeof r.full_name === 'string' ? r.full_name : '',
    email: typeof r.email === 'string' ? r.email : null,
    preferredLanguage: r.preferred_language === 'en' ? 'en' : 'ar',
    accountType,
    status,
    roles,
    permissions,
    primaryPortal: typeof r.primary_portal === 'string' ? r.primary_portal : null,
    // Older databases (before …_access_model.sql) return no global list: every permission was global.
    globalPermissions: Array.isArray(r.global_permissions) ? (strings(r.global_permissions) as PermissionCode[]) : permissions,
    scopes,
    bundles: strings(r.bundles),
    inheritedRoles: strings(r.inherited_roles).filter(isRole),
    passwordResetRequired: r.password_reset_required === true,
    preview: null,
  };
}

export function isRoleCode(value: unknown): value is RoleCode {
  return isRole(value);
}

export function hasPermission(access: AccessProfile | null, permission: PermissionCode): boolean {
  return !!access && access.status === 'active' && access.permissions.includes(permission);
}

export function hasRole(access: AccessProfile | null, role: RoleCode): boolean {
  return !!access && access.status === 'active' && access.roles.includes(role);
}
